import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';

const formatInputDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const emptyForm = () => ({
  redemption_no: '',
  redemption_date: formatInputDate(),
  quantity: '',
  reference_note: '',
});

const formatQuantity = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '-';
};

function StockRedemption() {
  const [stockRows, setStockRows] = useState([]);
  const [selectedStock, setSelectedStock] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loadingStock, setLoadingStock] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const loadStock = useCallback(async () => {
    setLoadingStock(true);
    try {
      const response = await axios.get('https://serverdemo.ccsoftwares.com/stock/getShop1Stock');
      setStockRows(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Unable to load available stock.');
    } finally {
      setLoadingStock(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const response = await axios.get('https://serverdemo.ccsoftwares.com/stock-redemptions/history', {
        params: {
          page: page + 1,
          pageSize: rowsPerPage,
          search: search || undefined,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
        },
      });
      setHistory(response.data?.rows || []);
      setHistoryTotal(Number(response.data?.total || 0));
    } catch (error) {
      setHistory([]);
      setHistoryTotal(0);
      setHistoryError(error.response?.data?.error || 'Unable to load redemption history.');
    } finally {
      setHistoryLoading(false);
    }
  }, [fromDate, page, rowsPerPage, search, toDate]);

  useEffect(() => {
    loadStock();
  }, [loadStock]);

  useEffect(() => {
    const timer = setTimeout(loadHistory, 250);
    return () => clearTimeout(timer);
  }, [loadHistory]);

  const availableStock = Number(selectedStock?.stock || 0);
  const quantity = Number(form.quantity);
  const quantityInvalid = form.quantity !== '' && (!Number.isFinite(quantity) || quantity <= 0 || quantity > availableStock);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedStock) {
      toast.error('Select an inventory item.');
      return;
    }
    if (!Number.isFinite(quantity) || quantity <= 0) {
      toast.error('Quantity must be greater than zero.');
      return;
    }
    if (quantity > availableStock) {
      toast.error(`Only ${formatQuantity(availableStock)} units are available.`);
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post('https://serverdemo.ccsoftwares.com/stock-redemptions', {
        redemption_no: form.redemption_no.trim() || undefined,
        redemption_date: form.redemption_date,
        reference_note: form.reference_note.trim() || undefined,
        items: [{ stock_id: selectedStock.stockId, quantity }],
      });
      toast.success(`Stock redeemed successfully: ${response.data.redemption_no}`);
      setForm(emptyForm());
      setSelectedStock(null);
      setPage(0);
      await Promise.all([loadStock(), loadHistory()]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Unable to redeem stock.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ px: { xs: 2, md: 4 }, pt: 12, pb: 4 }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
        Stock Redemption
      </Typography>

      <Card sx={{ mb: 4 }}>
        <CardHeader
          title="Redeem Inventory"
          subheader="Record an independent stock-out transaction. Billing does not change inventory."
        />
        <CardContent>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Redemption No. (optional)"
                  value={form.redemption_no}
                  onChange={(event) => setForm((current) => ({ ...current, redemption_no: event.target.value }))}
                  helperText="Leave blank to generate automatically"
                  inputProps={{ maxLength: 50 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  type="date"
                  label="Redemption Date"
                  value={form.redemption_date}
                  onChange={(event) => setForm((current) => ({ ...current, redemption_date: event.target.value }))}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Autocomplete
                  options={stockRows}
                  loading={loadingStock}
                  value={selectedStock}
                  isOptionEqualToValue={(option, value) => option.stockId === value.stockId}
                  getOptionLabel={(option) => `${option.itemname || option.itemName} — ${formatQuantity(option.stock)} available`}
                  onChange={(_event, value) => {
                    setSelectedStock(value);
                    setForm((current) => ({ ...current, quantity: '' }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label="Inventory Item"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingStock ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  required
                  type="number"
                  label="Quantity Redeemed"
                  value={form.quantity}
                  error={quantityInvalid}
                  helperText={selectedStock ? `Available: ${formatQuantity(availableStock)}` : 'Select an item first'}
                  inputProps={{ min: 0.01, max: availableStock || undefined, step: 0.01 }}
                  onChange={(event) => setForm((current) => ({ ...current, quantity: event.target.value }))}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Reference / Note"
                  value={form.reference_note}
                  onChange={(event) => setForm((current) => ({ ...current, reference_note: event.target.value }))}
                  inputProps={{ maxLength: 500 }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button type="submit" variant="contained" disabled={saving || quantityInvalid || !selectedStock}>
                  {saving ? 'Redeeming…' : 'Redeem Stock'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardHeader title="Stock Redemption History" />
        <CardContent>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Search reference, item, user or note"
                value={search}
                onChange={(event) => { setSearch(event.target.value); setPage(0); }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="From Date"
                value={fromDate}
                onChange={(event) => { setFromDate(event.target.value); setPage(0); }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="To Date"
                value={toDate}
                onChange={(event) => { setToDate(event.target.value); setPage(0); }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {historyError ? <Alert severity="error" sx={{ mb: 2 }}>{historyError}</Alert> : null}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Redemption No.</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell align="right">Quantity</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Reference / Note</TableCell>
                  <TableCell>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyLoading ? (
                  <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={26} /></TableCell></TableRow>
                ) : history.length ? history.map((row) => (
                  <TableRow key={row.redemption_detail_id}>
                    <TableCell>{row.redemption_date}</TableCell>
                    <TableCell>{row.redemption_no}</TableCell>
                    <TableCell>{row.item_name}</TableCell>
                    <TableCell align="right">{formatQuantity(row.quantity)}</TableCell>
                    <TableCell>{row.created_by}</TableCell>
                    <TableCell>{row.reference_note || '-'}</TableCell>
                    <TableCell>{row.created_at ? new Date(row.created_at).toLocaleString() : '-'}</TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={7} align="center">No redemption records found.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={historyTotal}
            page={page}
            onPageChange={(_event, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </CardContent>
      </Card>
    </Box>
  );
}

export default StockRedemption;
